// @ts-nocheck

import type { AppDispatch, RootState } from 'src/store'
import { Button, ButtonGroup, Col, Dropdown, DropdownButton, FloatingLabel, Form, InputGroup, Row } from 'react-bootstrap'
import { calculateProcessingFee, dateTitles, dates, daysOfMonths, isGiftOptionOptedOut, monthTitles, parseSubscription } from 'modules/utils'
import { donationTypeNames, isPaymentDateMenuVisible, sitesWithDonateDescription, siteDonateHeadlineText, siteOptionHighlightText, siteOnlyShowTypes } from 'modules/siteContents'
import { siteInitialAmounts } from 'modules/constants'
import {
  injectPlan,
  inputDonationAmount,
  setDonationAmount,
  setDonationType,
  setMonthlyOtherAmount,
  setPaymentDate,
  setPaymentMonth,
  setValidationError,
} from 'actions/optionsActions'
import { setData } from 'actions/contactActions';
import { setShowDetails, switchView } from 'slices/rootSlice'

import type { AppRouteProps } from 'src/types/app'
import type { ConnectedProps } from 'react-redux'
import React from 'react'
import ReactPlayer from 'react-player'
import Spinner from 'sharedComponents/Spinner'
import _ from 'lodash'
import { connect } from 'react-redux'
import { getCookie } from 'modules/cookie'
import moment from 'moment-timezone'
import numeral from 'numeral'
import queryString from 'query-string'
import { setIncludeProcessingFee } from 'actions/paymentActions'
import { withRouter } from 'src/routes/WithRouter'
import GiftMenu from '../../components/GiftMenu'
import { expressFlowMainImage } from 'modules/siteContents'

const cidTextDict = {
  STS: {
    '7018X000001uAsvQAE': ' - you can be certain your donation is effective. Your gift will help fund a flight of life-affirming nurses fully equipped to provide live chat for mobile medical clinics and pregnancy health clinics. With your first gift of any amount, we’ll send you a ',
    '7018X000001yOSEQA2': ' - you can be certain your donation is effective. Your gift will help fund a flight of life-affirming nurses fully equipped to provide live chat for mobile medical clinics and pregnancy health clinics. With your first gift of any amount, we’ll send you a ',
    '7018X000001yOS9QAM': ' - you can be certain your donation is effective. Your gift will help fund a flight of life-affirming nurses fully equipped to provide live chat for mobile medical clinics and pregnancy health clinics. With your first gift of any amount, we’ll send you a ',
  }
}

const mapState = (store : RootState, ownProps: AppRouteProps) => ({
  showDetails: store.root.showDetails,
  plans: ownProps.plans || store.options.plans,
  customer: store.root.customer,
  loadingCustomer: store.root.loadingCustomer,
  donationType: store.options.donationType,
  donationAmount: store.options.donationAmount,
  currentDonationPlan: store.options.currentDonationPlan,
  validationError: store.options.validationError,
  monthlyOtherAmount: store.options.monthlyOtherAmount,
  paymentDate: store.options.paymentDate,
  paymentMonth: store.options.paymentMonth,
  displayValidationError: store.payment.displayValidationError,
  onlyShowTypes: ownProps.onlyShowTypes || store.options.onlyShowTypes,
  includeProcessingFee: store.payment.includeProcessingFee,
  processingFee: calculateProcessingFee(store.options.donationAmount, null, store.payment.type),
  subscription: store.root.subscription ? {
    ...store.root.subscription,
    ...parseSubscription(store.root.subscription, store.root.crm),
  } : null,
  subscriptionYearly: store.root.subscriptionYearly ? {
    ...store.root.subscriptionYearly,
    ...parseSubscription(store.root.subscriptionYearly, store.root.crm),
  } : null,
  campaignId: store.contact.campaignId,
  headerVideo: (store.root.campaigns.find(c => c.id === store.contact.campaignId) || {}).headerVideo,
  utmVideo: Object.keys(_.get(ownProps, 'initialUrlParams', {})).reduce((acc, cur) => {
    return acc || _.get(store.root.utmVideos, [cur, _.get(ownProps, ['initialUrlParams', cur])])
  }, null),
  charityRun: store.charityRun,
  eventRegistration: store.eventRegistration,
  campaignOptions: (store.root.campaigns.find(c => c.id === store.contact.campaignId) || {}).options,
  selectedCampaignOption: store.contact.selectedCampaignOption,
})

const actionCreators = {
  // switchView,
  setDonationType,
  setDonationAmount,
  inputDonationAmount,
  setValidationError,
  setShowDetails,
  setMonthlyOtherAmount,
  setPaymentDate,
  setPaymentMonth,
  setIncludeProcessingFee,
  injectPlan,
  setData,
}

const connector = connect(mapState, actionCreators)
type OptionsProps = ConnectedProps<typeof connector> & AppRouteProps;
interface OptionsState {
  showTaxStatus: boolean,
  giftModalOpen: boolean,
  decimal: string,
  editingOtherAmount: boolean,
}

class Options extends React.Component<OptionsProps, OptionsState> {
  state: OptionsState = {
    showTaxStatus: false,
    giftModalOpen: false,
    decimal: '',
    editingOtherAmount: false,
  }

  /**
   * Blur on wheel (capture + passive) so the focused number input does not step the value.
   * We avoid preventDefault so the same wheel gesture can still scroll the page.
   */
  otherAmountWheelCleanup: (() => void) | null = null

  handleOtherAmountInputRef = (el: HTMLInputElement | null) => {
    if (this.otherAmountWheelCleanup) {
      this.otherAmountWheelCleanup()
      this.otherAmountWheelCleanup = null
    }
    if (!el) return
    const onWheel = () => {
      el.blur()
    }
    el.addEventListener('wheel', onWheel, { passive: true, capture: true })
    this.otherAmountWheelCleanup = () => el.removeEventListener('wheel', onWheel, { capture: true })
  }

  componentWillUnmount() {
    if (this.otherAmountWheelCleanup) {
      this.otherAmountWheelCleanup()
      this.otherAmountWheelCleanup = null
    }
  }

  /**
   * Normalize free-form custom amount input into a parseable numeric string.
   *
   * Donors routinely type large gifts with separators (e.g. "$10,000" or
   * "10 000"). A native number input would sanitize such buffers to "" and the
   * field would force-reset with a spurious minimum-amount warning, so we keep
   * the input as text and strip everything except digits and a single decimal
   * point here instead.
   */
  sanitizeAmountInput = (raw) => {
    let sanitized = (raw == null ? '' : String(raw)).replace(/[^0-9.]/g, '')
    const firstDot = sanitized.indexOf('.')
    if (firstDot !== -1) {
      sanitized =
        sanitized.slice(0, firstDot + 1) +
        sanitized.slice(firstDot + 1).replace(/\./g, '')
    }
    return sanitized
  }

  /** True when donationAmount matches a preset button for this term (not "Other"). */
  syncPresetAmountSelection = (donationType, donationAmount, plans) => {
    const numericAmount = Number(donationAmount)
    if (!donationAmount || isNaN(numericAmount)) {
      this.props.setMonthlyOtherAmount(true)
      return false
    }
    const presetAmounts = (plans[donationType] || []).filter(a => a != null).map(Number)
    const matchesPreset = presetAmounts.includes(numericAmount)
    this.props.setMonthlyOtherAmount(!matchesPreset)
    return matchesPreset
  }

  /**
   * 
   */
  componentDidMount() {
    const {
      // donationAmount, 
      donationType, 
      initialUrlParams,
      guestMode,
      plans, 
      setDonationType, 
      setDonationAmount, 
      setMonthlyOtherAmount, 
      setPaymentDate,
      subscription, 
      injectPlan,
      enableEvents,
      layoutConfig,
      site,
      initialDonationType,
      defaultAmounts,
    } = this.props
    const {
      term = 'monthly',
      amount,
      showTaxStatus,
      // isOther,
      isMonthly,
    } = initialUrlParams

    // Use layout / prop initialDonationType if provided, otherwise use term from URL
    const effectiveTerm = layoutConfig?.initialDonationType || initialDonationType || term
    const effectiveAmount = ((initialDonationType && !amount) || plans.skip_initial_param_amount)
      ? (plans[`default_${initialDonationType}_amount`] || 25)
      : amount

    if (guestMode) {
      // this.selectDonationType('one-time')
    }

    const planTerm = plans[effectiveTerm] || []
    // const planTermIndex = (isMonthly && planTerm.length) > 2 ? 1 : 0
    // console.log(plans, term, planTermIndex)
    const amountKey = `amount${effectiveTerm?.charAt(0)}`
    const planAmount = (
      defaultAmounts?.[effectiveTerm] ||
      siteInitialAmounts[site]?.[effectiveTerm] || 
      effectiveAmount ||
      plans[`default_${effectiveTerm}_amount`]
    )

    // Inject new plan if planAmount is available from plan presets
    if (planAmount !== 'other' && plans[effectiveTerm] && !plans[effectiveTerm].includes(planAmount)) {
      injectPlan(planAmount, effectiveTerm)
    }

    setDonationType(effectiveTerm)
    setDonationAmount(Number(planAmount))
    this.props.router.standardizeDonateRoute(effectiveTerm, planAmount, { replace: true })

    if (!planAmount) {
      setMonthlyOtherAmount(true)
      this.focusOtherAmountInput()
    } else {
      const matchesPreset = this.syncPresetAmountSelection(effectiveTerm, planAmount, plans)
      if (!matchesPreset) {
        this.focusOtherAmountInput()
      }
    }
    if (showTaxStatus) {
      this.setState({ showTaxStatus: true })
    }
    if (subscription) {
      const paymentDate = new Date(subscription.nextPaymentDate || subscription.startDate)
      setPaymentDate(paymentDate.getUTCDate() || (new Date()).getDate())
    }
    else if (site === 'VV') {
      // VV never shows the payment-date picker; avoid stale localStorage (e.g. day-of-month 1).
      setPaymentDate((new Date()).getDate())
    }

    if (['monthly', 'yearly'].includes(donationType)) {
      this.syncPaymentDateWithSubscription()
    }

    if (layoutConfig?.roundUpCheckbox?.defaultValue !== undefined) {
      this.props.setIncludeProcessingFee(layoutConfig.roundUpCheckbox.defaultValue)
    }
  }

  /**
   * 
   * @param prevProps 
   * @param prevState 
   * @param snapshot 
   */
  componentDidUpdate(prevProps: Readonly<OptionsProps>, prevState: Readonly<OptionsState>, snapshot?: any): void {
    const {
      subscription, 
      setPaymentDate, 
      donationType, 
      donationAmount, 
      plans,
      initialUrlParams,
      layoutConfig,
      defaultAmounts,
    } = this.props
    const {
      amount,
      isMonthly,
    } = initialUrlParams

    if (!prevProps.subscription && subscription) {
      const paymentDate = new Date(subscription.nextPaymentDate || subscription.startDate)
      setPaymentDate(paymentDate.getUTCDate() || (new Date()).getDate())
    }
    const amountKey = `amount${donationType?.charAt(0)}`
    if ((prevProps.donationType !== donationType) || (prevProps.donationAmount !== donationAmount)) {
      const setAmount = prevProps.donationType !== donationType ? (
        Number(defaultAmounts?.[donationType] || plans[`default_${donationType}_amount`])
      ) : (
        donationAmount || initialUrlParams[amountKey] || amount
      )

      setDonationType(donationType)
      setDonationAmount(Number(setAmount))
      this.props.router.standardizeDonateRoute(donationType, setAmount)      
    }
    
    if ((prevProps.donationType !== this.props.donationType) && ['monthly', 'yearly'].includes(this.props.donationType)) {
      this.syncPaymentDateWithSubscription()
    }

    if (prevProps.layoutConfig?.roundUpCheckbox?.defaultValue === undefined && layoutConfig?.roundUpCheckbox?.defaultValue !== undefined) {
      this.props.setIncludeProcessingFee(layoutConfig.roundUpCheckbox.defaultValue)
    }

    // While the donor is actively typing in the "Other" field, don't re-sync
    // the preset selection: an in-progress value can transiently equal a preset
    // (e.g. one-time presets include 100 and 1000, so typing "10000" passes
    // through them) which would flip out of "Other" mode and wipe the input.
    const prevPresetKey = (prevProps.plans[donationType] || []).filter(a => a != null).join(',')
    const nextPresetKey = (plans[donationType] || []).filter(a => a != null).join(',')
    if (
      !this.state.editingOtherAmount &&
      (
        prevProps.plans !== plans ||
        prevPresetKey !== nextPresetKey ||
        prevProps.donationType !== donationType ||
        prevProps.donationAmount !== donationAmount
      )
    ) {
      this.syncPresetAmountSelection(donationType, donationAmount, plans)
    }
  }

  submitOptions = (donationAmount) => {
    if (donationAmount) {
      this.props.setDonationAmount(donationAmount)
      this.props.setValidationError({ donationAmount: '' })
    }
    else {
      this.props.setValidationError({ donationAmount: 'This field is required' })
    }
  } 

  selectDonationType = donationType => {
    const { setDonationType, setDonationAmount, plans } = this.props
    setDonationType(donationType)
    if (donationType !== this.props.donationType) {
      // const isMonthly = donationType === 'monthly'
      setDonationAmount(Number(plans[`default_${donationType}_amount`]))
    }
  }

  formatChargedPaymentProfile = subscription => {
    let creditCard, bankAccount
    switch (subscription.paymentGateway) {
      case 'Authorize.NET':
        creditCard = _.get(subscription, ['profile', 'paymentProfile', 'payment', 'creditCard'])
        bankAccount = _.get(subscription, ['profile', 'paymentProfile', 'payment', 'bankAccount'])
        return (
          creditCard && `credit card ${creditCard.cardNumber}`
        ) || (
          bankAccount && `bank account ${bankAccount.accountNumber} (routing ${bankAccount.routingNumber}, holder ${bankAccount.nameOnAccount})`
        )
      default:
        if (!subscription.payment) {
          return `an expired credit card / bank account`
        }
        if (subscription.payment.object === 'payment_method') {
          switch (subscription.payment.type) {
            case 'card':
              return `credit card XXXX ${subscription.payment.card.last4}`
            case 'us_bank_account':
              return `bank account ${subscription.payment.us_bank_account.last4} (${subscription.payment.us_bank_account.bank_name})`
            default:
              return ''
          }
        }
        return subscription.payment.object === 'card' ? (
          `credit card XXXX ${subscription.payment.last4}`
        ) : (
          `bank account ${subscription.payment.last4} (routing ${subscription.payment.routing_number}` +
          (subscription.payment.account_holder_name ? `, holder ${subscription.payment.account_holder_name}` : '') +
          ')'
        )
    }
  }   
  syncPaymentDateWithSubscription = () => {
    const { donationType, setPaymentDate, setPaymentMonth } = this.props
    const subscription = this.props[donationType === 'yearly' ? 'subscriptionYearly' : 'subscription']
    if (subscription && subscription.npe03__next_payment_date__c) {
      const nextPaymentDate = moment(subscription.npe03__next_payment_date__c)
      setPaymentDate(nextPaymentDate.date())
      if (donationType === 'yearly') {
        setPaymentMonth(nextPaymentDate.month())
      }
    }
  }



  focusOtherAmountInput = () => {
    setTimeout(() => {
      const otherAmountInput = document.getElementById('other-amount-input')
      const donateTypeContainer = document.getElementById('donate-type-container')

      if (donateTypeContainer && otherAmountInput) {
        donateTypeContainer.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
          
        // Back up
        // const fakeInput = document.createElement('input')
        // fakeInput.setAttribute('type', 'text')
        // fakeInput.style.position = 'absolute'
        // fakeInput.style.opacity = 0
        // fakeInput.style.height = 0
        // fakeInput.style.fontSize = '16px' // disable auto zoom
        // document.body.prepend(fakeInput)
        // setTimeout(() => {
        //   otherAmountInput.dispatchEvent(new Event('touchstart'))
        //   otherAmountInput.focus()
        //   // otherAmountInput.click()
        //   fakeInput.remove()
        // }, 100)

        const f = (event) => {
          setTimeout(() => { 
            otherAmountInput.focus()
              event.preventDefault()
          })
        }
        let mobile = false
        donateTypeContainer.addEventListener('click', (event) => {
            if(mobile) return
            f(event)
        })

        donateTypeContainer.addEventListener('touchstart', (event) => {
            mobile = true
            f(event)
        })

        donateTypeContainer.addEventListener('touchend', (event) => {
            event.preventDefault()
            event.stopPropagation()
        })

        donateTypeContainer.click()
      }
    }, 100)
  }

  giftDescription = (site, cid, flow) => {
    switch (site) {
      case 'STS':
        if (flow === 'express') {
          return (
            <p>
              {`Thank you for donating in support of Charlie's Save the Storks Mobile Medical Clinic.`}
            </p>
          )
        }
        return (
          <p>
            {`Because 4 out of 5 women who board a Stork Bus choose life`}
            <sup>1</sup>
            {
              cidTextDict.STS[cid] ||
              ` - you can be certain your donation is effective. Your gift will help fund more Stork Buses and keep their services FREE of charge to the women who most need them. With your first gift of any amount, we’ll send you a `
            }
            {/* <span id="gift-banner-text">Save the Storks sticker (pictured right)</span> */}
            {/* <a id="gift-banner-anchor" href="#save-the-storks-gift">Save the Storks sticker</a> */}
            {`Save the Storks sticker`}
            {` as a way to remember the life-saving work you are accomplishing. Thank you for your generosity today!`}
          </p>
        )
      case 'IHH':
        return (
          <p style={{paddingTop: '20px'}}>
            When you donate to In His Hands, you are directly impacting the spread of hope and healing to hundreds of women who have experienced the devastating loss of a child.
          </p>
        )
      default:
        return null
    }
  }

  

  render() {
    const urlQuery = queryString.parse(location.search)
    const { donationType = urlQuery.term || 'monthly', plans } = this.props
    const isMonthly = donationType === 'monthly'
    const {
      customer,
      loadingCustomer,
      donationAmount = Number(urlQuery.amount || plans[donationType][isMonthly ? 1 : 0]),
      validationError,
      inputDonationAmount,
      setDonationAmount,
      subscription,
      subscriptionYearly,
      showDetails,
      setShowDetails,
      monthlyOtherAmount,
      setMonthlyOtherAmount,
      paymentDate,
      paymentMonth,
      setPaymentDate,
      setPaymentMonth,
      displayValidationError,
      onlyShowTypes,
      guestMode,
      processingFee,
      includeProcessingFee,
      setIncludeProcessingFee,
      site,
      initialUrlParams = {},
      campaignId,
      isAdminTerminal,
      utmVideo,
      headerVideo,
      charityRun: { attendees, ready: charityRunReady },
      optionsErrors,
      layoutConfig,
      campaignOptions,
      selectedCampaignOption,
      setData,
      flow,
    } = this.props
    const errors = optionsErrors || []
    const {
      showTaxStatus,
      giftModalOpen,
      decimal,
    } = this.state
    const showPaymentDateMenu = isPaymentDateMenuVisible({ site, layoutConfig, guestMode })
    const donationTypes = siteOnlyShowTypes[site] ? siteOnlyShowTypes[site] : (onlyShowTypes.length ? onlyShowTypes : Object.keys(donationTypeNames))
    const reversedDonationTypes = donationTypes.map((t, index) => donationTypes[donationTypes.length - index - 1])

    if (!plans) return <Spinner />

    let roundedUp;
    if (donationAmount !== 'other') {
      roundedUp = (Number(donationAmount) + Number(processingFee)).toFixed(2)
    }

    let minimumRequired = charityRunReady ? 35 * attendees.length : 5;
    if (!minimumRequired) minimumRequired = 35

    const headlineLayoutConfig = layoutConfig?.headline || {}
    const giftDescriptionLayoutConfig = (layoutConfig || {})[`gift_descriptions_${donationType}`] || layoutConfig?.giftDescriptions
    const giftOptions = campaignOptions || layoutConfig?.giftOptions
    const campaignOptionMenu = (giftOptions && !!layoutConfig && selectedCampaignOption) ? (
      <div>
        <GiftMenu
          giftOptions={giftOptions}
          selectedCampaignOption={selectedCampaignOption}
          onClick={option => {
            const nextSelectedCampaignOption = { ...selectedCampaignOption, option }
            setData({ field: 'selectedCampaignOption', val: nextSelectedCampaignOption })
            if (
              giftOptions.requireAddressWhenOptedIn
              && !isGiftOptionOptedOut({ ...nextSelectedCampaignOption, checkBox: giftOptions.checkBox })
            ) {
              setShowDetails(true)
            }
          }}
          inline={true}
          />
      </div>
    ) : null
    const titleText = headlineLayoutConfig[`title_${donationType}`] || headlineLayoutConfig.title || (
      siteDonateHeadlineText[site] || 'Donate Now'
    )
    return (
      <section className='options'>
        <style>
          {`.item-break-2 {
            margin-right: 0px;
          }
          .break-2 {
            flex-basis: 100%;
          }
          .item-break-3 {
            margin-right: 12px !important;
          }
          .break-3 {
            flex-basis: 0%;
          }
          .donate-option-placeholder {
            display: none;
          }
          .donate-option-other-amount {
            margin-right: 0px !important;
          }`}
        </style>
        { loadingCustomer ? <Spinner mask={true} hideicon={true} /> : null }
        {
          guestMode ? (
            <Row className='pt2'>
              {
                flow === 'express' ? (
                  <Col xs={12} md={6} className='center'>
                    <img 
                      src={expressFlowMainImage[site]} 
                      style={{ width: '100%', maxWidth: 300, margin: 'auto', marginBottom: 20, borderRadius: 16, boxShadow: '5px 5px 10px 2px rgba(0, 0, 0, 0.5)' }} 
                    />
                  </Col>
                ) : null
              }
              <Col xs={12} md={ flow === 'express' ? 6 : 12 }>
                {
                  headlineLayoutConfig[`logo_${donationType}`] ? (
                    <img 
                      className='float-right'
                      src={headlineLayoutConfig[`logo_${donationType}`]} 
                      style={headlineLayoutConfig[`logo_${donationType}_style`] || null}
                    />
                  ) : null
                }
                {
                  flow === 'express' ? (
                    <img 
                      className='express-flow-main-logo-mobile'
                      src="/server/assets/images/main-logo.png" 
                      style={{ maxWidth: 144, margin: 'auto', marginTop: 10, float: 'right' }} 
                    />
                  ) : null
                }
                <h3 
                  className='donate-headline' 
                  style={{
                    ...(layoutConfig?.headline || {})[`title_${donationType}_style`] || layoutConfig?.headline?.titleStyle || null,
                    ...(flow === 'express' ? { color: '#fb534e' } : null)
                  }}
                >
                  {
                    titleText.split('|').map((p,pi) => <span key={pi}>{p.startsWith('\\b') ? <b>{p.slice(2)}</b> : p.startsWith('\\i') ? <i>{p.slice(2)}</i> : p}</span>)
                  }
                </h3>
                {
                  headlineLayoutConfig.subtitle ? (
                    <h4 className='donate-headline-subtitle' style={headlineLayoutConfig[`subtitle_${donationType}_style`] || headlineLayoutConfig.subtitleStyle || null}>
                      {headlineLayoutConfig[`subtitle_${donationType}`] || headlineLayoutConfig.subtitle}
                    </h4>
                  ) : null
                }
                {
                  headlineLayoutConfig[`hero_image_${donationType}`] ? (
                    <img 
                      src={headlineLayoutConfig[`hero_image_${donationType}`]}
                      style={headlineLayoutConfig[`hero_image_${donationType}_style`]}
                    />
                  ) : null
                }
                {
                  (headerVideo || utmVideo) ? (
                    <ReactPlayer 
                      id='videoPlayer' 
                      className="mb2"
                      controls width='100%' height='unset'
                      url={headerVideo || utmVideo} 
                      style={{ aspectRatio: '16/9' }}
                    />
                  ) : null
                }
                {
                  giftDescriptionLayoutConfig ? giftDescriptionLayoutConfig.map((p,i) => (
                    Array.isArray(p) ? (
                      <ul key={i}>
                        {p.map((p,pi) => <li key={pi}>{p.split('|').map((p,pi) => <span key={pi}>{p.startsWith('\\b') ? <b>{p.slice(2)}</b> : p.startsWith('\\i') ? <i>{p.slice(2)}</i> : p}</span>)}</li>)}
                      </ul>
                    ) : (
                      <p key={i}>
                        {p.split('|').map((p,pi) => <span key={pi}>{p.startsWith('\\b') ? <b>{p.slice(2)}</b> : p.startsWith('\\i') ? <i>{p.slice(2)}</i> : p}</span>)}
                      </p>
                    )
                  )) : (
                    sitesWithDonateDescription.includes(site) && !isAdminTerminal && (
                      this.giftDescription(site, campaignId || initialUrlParams.cid || getCookie('cid'), flow)
                    )
                  )
                }
                {
                  flow === 'express' ? (
                    <img 
                      className='express-flow-main-logo-desktop'
                      src="/server/assets/images/main-logo.png" 
                      style={{ maxWidth: 144, marginTop: 10 }} 
                    />
                  ) : null
                }
              </Col>
              <br />
            </Row>
          ) : (
            <div>
              {
                headerVideo ? (
                  <ReactPlayer 
                    id='videoPlayer' 
                    className="mt3"
                    controls width='100%' height='unset'
                    style={{ aspectRatio: '16/9' }}
                    url={headerVideo} 
                  />
                ) : null
              }
              <legend style={showTaxStatus ? {paddingBottom: 0, marginBottom: 0} : {}}>Donation Amount</legend>
            </div>
          )
        }
        {
          showTaxStatus ? (
            <div style={{ marginBottom: 40, paddingTop: 2 }}>
              <small>Save the Storks® is a 501 (c)(3) EIN: 461031815</small>
            </div>
        ): null
        }
        {
          (subscription && (donationType === 'monthly')) ? (
            <p>
              <br />
              <span>
                {`You are currently donating $${(subscription.amount || 0).toFixed(2)} per month using ${this.formatChargedPaymentProfile(subscription)}.`}
              </span>
            </p>
          ) : null
        }
        {
          (subscriptionYearly && (donationType === 'yearly')) ? (
            <p>
              <br />
              <span>
                {`You are currently donating $${(subscriptionYearly.amount || 0).toFixed(2)} per year using ${this.formatChargedPaymentProfile(subscriptionYearly)}.`}
              </span>
            </p>
          ) : null
        }
        {
          layoutConfig?.donationTypeMenu?.visible !== false ? (
            <Form.Group id="donate-type-container">
              <div className="flex pb-3">
                {
                  reversedDonationTypes.map((key, index) => (
                    <Button
                      key={key}
                      style={index ? { marginLeft: 12 } : null}
                      className={`flex-1 btn-option ${donationType === key ? 'selected' : ''}`}
                      onClick={() => this.selectDonationType(key)}
                    >
                      {donationTypeNames[key]}
                    </Button>
                  ))
                }
              </div>
            </Form.Group>
          ) : null
        }
        {
          guestMode && (layoutConfig?.amountImpact?.visible !== false) && (flow !== 'express') && (
            <Form.Group className="right option-highlight" style={{margin: '10px 0px'}}>
              {siteOptionHighlightText[site] ? (
                <span className='hightlighted' style={{ float: 'left' }}>{siteOptionHighlightText[site]}</span>
              ) : null}
              <span className='hightlighted'>{`${Number(donationAmount) ? ('$' + donationAmount) : 'Your donation'} would have a huge impact!`}</span>
            </Form.Group>
          )
        }
        <Form.Group className={`flex flex-wrap${errors.some(e => e.field === 'donationAmount') ? ' has-error' : ''}`}>
        {
            plans[donationType || 'monthly'].reduce((acc, amount, index) => {
              const break2 = !((index+1) % 2), break3 = !((index+1) % 3)
              const breakClassName = `break${break2 ? '-2' : ''}${break3 ? '-3' : ''}`
              const lastItem = index === plans[donationType].length - 1
              const text = (plans[`${donationType}_text`] || [])[index]
              return [
                ...acc,
                (
                  <div 
                    style={layoutConfig?.amountButtonStyle || null}
                    className={`donate-option ${amount ? '' : 'donate-option-other-amount'} ${(break2 || break3) ? 'item-' + breakClassName : ''}`} 
                    key={index}
                  >
                    {
                      amount ? (
                        <Button
                          className={`full-width btn-option ${(monthlyOtherAmount ? false : (Number(donationAmount) === Number(amount))) ? 'selected' : ''}`}
                          onClick={() => {
                            console.log('setting donation amount 5', amount)
                            setDonationAmount(amount)
                            setMonthlyOtherAmount(false)
                          }}
                        >
                          {`$${numeral(amount).format('0,0')}${text ? ` - ${text}` : ''}`}
                        </Button>
                      ) : (
                        <>
                        {/* <InputGroup 
                          style={{ height: 44}} 
                          className={(displayValidationError && !donationAmount) ? 'has-error' : ''}
                          size='xs'
                        > */}
                        {/* <InputGroup.Text>$</InputGroup.Text> */}
                          {/* <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                            </div> */}
                            <FloatingLabel label="Other"
                              className={`option-other ${monthlyOtherAmount ? 'selected' : ''}`}
                            >
                            <div className="left-inner-addon">
                            <span>$</span>
                            </div>
                            <Form.Control
                              id="other-amount-input"
                              ref={this.handleOtherAmountInputRef}
                              // style={{ height: 44 }}
                              placeholder="Other"
                              type="text"
                              inputMode="decimal"
                              name="donationAmount"
                              value={monthlyOtherAmount ? (((donationAmount || '').toString()) || '').replace(decimal, '') + decimal : ''}
                              onChange={(e) => {
                                if (!monthlyOtherAmount) {
                                  setMonthlyOtherAmount(true)
                                  inputDonationAmount(null)
                                }
                                const sanitized = this.sanitizeAmountInput(e.target.value)
                                const nextDecimal = ((sanitized.match(/\.\d*/) || [])[0] || '').slice(0,3)
                                if (nextDecimal.length > 3) return
                                if (nextDecimal.length == 3 && nextDecimal[2] === '0') return
                                this.setState({ decimal: nextDecimal })
                                inputDonationAmount(sanitized === '' ? null : sanitized)
                                e.currentTarget.classList.remove('is-invalid')
                                e.currentTarget.parentElement?.parentElement?.classList.remove('has-error')
                              }}
                              onFocus={() => {
                                this.setState({ editingOtherAmount: true })
                              }}
                              onBlur={e => {
                                this.setState({ decimal: '', editingOtherAmount: false })
                              }}
                              isInvalid={errors.some(e => e.field === 'donationAmount')}
                            />
                            <Form.Control.Feedback type="invalid">{errors.find(e => e.field === `donationAmount`)?.message[0]}</Form.Control.Feedback>
                            </FloatingLabel>
                        {/* </div> */}
                        {/* </InputGroup> */}
                        </>
                      )
                    }
                  </div>
                ),
                (lastItem && !(break2 || break3)) ? <div className="flex-1 donate-option-placeholder" key={`flex-1-${index}`}>&nbsp;</div> : null,
                ((break2 || break3) && !lastItem) ? (
                  <div key={`break-${index}`} className={breakClassName}/>
                ) : null
              ]
            }, [])
          }
          { 
            monthlyOtherAmount && (donationAmount < minimumRequired) && (
              <div className="mt1 full-flex" style={{ color: '#a94442' }}>
                {`Please check and re-enter the donation amount (minimum $${minimumRequired})`}
              </div>
            )
          }
        </Form.Group>
        {
          ((donationType === 'monthly') && showPaymentDateMenu) ? (
            <div>
              <label className="pre-field required mr2">Payment date on:</label>
              <DropdownButton
                title={dateTitles[paymentDate - 1]}
                id="payment-date-menu"
                className='d-inline-flex'
              >
                {
                  dates.map((d, i) => (
                    <Dropdown.Item
                      key={i}
                      onClick={() => setPaymentDate(d)}
                      active={d === paymentDate}
                    >
                      {dateTitles[i]}
                    </Dropdown.Item>
                  ))
                }
              </DropdownButton>
              <label className="pre-field ml2">of every month.</label>
            </div>
          ) : null
        }
        {
          ((donationType === 'yearly') && showPaymentDateMenu) ? (
            <div>
              <label className="pre-field required mr2">Payment date on:</label>
              <DropdownButton
                title={dateTitles[paymentDate - 1]}
                id="payment-date-menu"
                className='d-inline-flex'
              >
                {
                  dates.slice(0, daysOfMonths[paymentMonth]).map((d, i) => (
                    <Dropdown.Item
                      key={i}
                      onClick={() => setPaymentDate(d)}
                      active={d === paymentDate}
                    >
                      {dateTitles[i]}
                    </Dropdown.Item>
                  ))
                }
              </DropdownButton>
              &nbsp;
              <DropdownButton
                title={monthTitles[paymentMonth]}
                id="payment-month-menu"
                className='d-inline-flex'
              >
                {
                  monthTitles.map((m, i) => (
                    <Dropdown.Item
                      key={i}
                      onClick={() => setPaymentMonth(i)}
                      active={m === paymentMonth}
                    >
                      {m}
                    </Dropdown.Item>
                  ))
                }
              </DropdownButton>
              <label className="pre-field ml2">of every year.</label>
            </div>
          ) : null
        }
        {
          (guestMode && (layoutConfig?.roundUpCheckbox?.visible !== false)) ? (
            <div>
              <Form.Check
                id="round-up"
                className="pre-field flex-children"
              >
                <Form.Check.Input
                  type='checkbox'
                  defaultChecked={includeProcessingFee} 
                  onChange={() => setIncludeProcessingFee(!includeProcessingFee)}
                />
                <Form.Check.Label>
                  <div className="flex v-center">
                    {`Round up my donation ${roundedUp} so the processing fees are covered`}
                  </div>
                </Form.Check.Label>
              </Form.Check>
            </div>
          ) : null
        }
        { campaignOptionMenu }
        {/* {
          customer ? (
            <div className="mt3">
              <Button className={showDetails ? 'btn-donate' : ''} onClick={() => setShowDetails(!showDetails)}>
                {`${showDetails ? 'Hide' : 'Show'} your profile details`}
              </Button>
            </div>
          ) : null
        } */}
      </section>
    )
  }
}

export default connector(withRouter(Options))